import * as motion from "motion/react-client";
import { useTranslation } from "react-i18next";

import { cn } from "../../lib/utils.js";

/** What just happened to the viewer's own side. */
export type Flash =
  /** They won the card. */
  | "won"
  /** They lost one, or failed to win one. */
  | "lost"
  /** A card was taken off them. */
  | "robbed";

/**
 * The moment something happens to your cards — phase 18, finding #8.
 *
 * # The complaint, verbatim
 *
 * > *"Visually when you fail or succeed you don't know — it's too subtle. It
 * > should be an animation or something to say you won this card, like a game."*
 *
 * Everything about winning and losing was **typographic**: `CORRECTA` in green
 * on an option, and a line reading *"Falló +1 🪙 Sin carta · artista"*. Both are
 * **status** — a description of a state — read at thirty centimetres, in a game
 * whose other screen is read at three metres. Nothing ever *happened*.
 *
 * # Why a separate component rather than a class on the card
 *
 * A win is an **event**, and the card is a **thing**. Attaching the celebration
 * to the card would make it a property of a card that is on screen, which is how
 * you end up with something that re-plays on every re-render and looks broken.
 * This mounts, plays once, and is gone — the caller keys it on the round so the
 * next one is a new element rather than a restarted animation.
 *
 * # ⚠️ One-shot on an event, never an idle loop
 *
 * `frontend.md` is explicit that the display is a ten-foot UI on an underpowered
 * TV browser: *"no layout thrash, no animation-heavy idle loop"*. So this is a
 * single spring on mount with nothing running afterwards, and it is
 * `pointer-events-none` so it can never take a tap from the control underneath.
 *
 * `prefers-reduced-motion` is honoured globally in `styles/index.css`, which is
 * why there is no check for it here — a second one would be a second place for
 * the answer to differ.
 *
 * # It says a word, not only a colour
 *
 * Same rule as `Card`: about one man in twelve cannot tell the green from the
 * red, and this is the one moment of the round that must land.
 */
export function OutcomeFlash({ flash }: { flash: Flash }) {
  const { t } = useTranslation();

  return (
    <motion.div
      data-testid="outcome-flash"
      data-flash={flash}
      role="status"
      aria-live="polite"
      initial={{ opacity: 0, scale: 0.7 }}
      animate={{ opacity: [0, 1, 1, 0], scale: [0.7, 1.08, 1, 1] }}
      transition={{ duration: 1.6, times: [0, 0.18, 0.7, 1] }}
      className="pointer-events-none flex justify-center py-2"
    >
      <span
        className={cn(
          "rounded-2xl border-2 px-6 py-3 text-2xl font-semibold tracking-tight",
          flash === "won" && "border-correct-500 bg-correct-500/15 text-correct-500",
          flash === "lost" && "border-wrong-500 bg-wrong-500/15 text-wrong-500",
          flash === "robbed" && "border-wrong-500 bg-wrong-500/20 text-wrong-500",
        )}
      >
        {flash === "won" && t("round.flash.won")}
        {flash === "lost" && t("round.flash.lost")}
        {flash === "robbed" && t("round.flash.robbed")}
      </span>
    </motion.div>
  );
}
